import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import {connect} from "react-redux";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Collapse from "@mui/material/Collapse";
import MCard from "components/Shared/MCard.tsx";
import { MSpinner } from "components/Shared";
import responseHandler from "responseHandler";
import WebConfig from "middlewares/WebConfig";
import { Notifications } from 'components/Homeowner/CaseForms/Validations/';
import { getCaseHeader } from "actions/homeowner/caseforms";
import { updateCaseHeader } from "requests/homeowner/caseforms/index";
import {
  setupSignalRConnection,
  SignalRConnectionStatus_Connected,
  SignalRConnectionStatus_Disconnected,
  SignalRConnectionStatus_Reconnecting,
} from "middlewares/signalrAspNet";
import { CASE_NOTIFICATIONS } from "typeConstants/rights";
import { getAvailableExports, getExportResults, startExport, markExportForRetry } from 'requests/caseExports';
import {getWaiverResults} from "requests/appraisal-survey";
import moment from "moment";
import {DATA_TYPE_BOOLEAN, DATA_TYPE_DATE_TIME, DATA_TYPE_MULTI_LINE_STRING, DATA_TYPE_STRING} from "typeConstants/dataTypes";
import {
  FANNIE_MAE_FIELD_SURVEY_FEATURE_DRIVEN_EXPORT,
  FREDDIEMAC_BACE_EXPORT,
  FNMA_UPD_FEATURE_DRIVEN_EXPORT,
  FREDDIE_MAC_UPD_FEATURE_DRIVEN_EXPORT,
} from "typeConstants/exportDefinitionCodes";
import { setRunningExports } from "actions/exports";

const Exports = ({
  controlNumber,
  initSignalR,
  signalrToken,
  makeToast,
  showNotifications,
  runningExports,
  setRunningExports,
  caseInfo,
  getCaseHeader,
}) => {

  const exportInfoDefaultState =
  {
    loanFreddieMacCaseFileId: caseInfo.loanFreddieMacCaseFileId,
    loanDesktopUnderwriterCaseFileId: caseInfo.loanDesktopUnderwriterCaseFileId,
    integrationFannieMaeWaiver: caseInfo.integrationFannieMaeWaiver,
    integrationFreddieMacBace: caseInfo.integrationFreddieMacBace,
  };

  const [fetchingExportDefs, setFetchingExportDefs] = useState(false);
  const [exportDefs, setExportDefs] = useState([]);
  const [fetchingExportResults, setFetchingExportResults] = useState(false);
  const [exportResults, setExportResults] = useState([]);
  const [runningExportsCurrent, setRunningExportsCurrent] = useState({});
  const [exportMessages, setExportMessages] = useState({});
  const [signalRConnected, setSignalRConnected] = useState(false);
  const [waiverResults, setWaiverResults] = useState({});
  const [fetchingWaiverResults, setFetchingWaiverResults] = useState(false);
  const [toggleFannieMaeCard, setToggleFannieMaeCard] = useState(true);
  const [toggleFreddieMacCard, setToggleFreddieMacCard] = useState(true);
  const [exportInfo, setExportInfo] = useState({...exportInfoDefaultState});
  const [isSavingCaseHeaders, setIsSavingCaseHeaders] = useState(false);

  const cardControlMethods = useRef();

  const fannieMaeExports = [FANNIE_MAE_FIELD_SURVEY_FEATURE_DRIVEN_EXPORT, FNMA_UPD_FEATURE_DRIVEN_EXPORT];
  const freddieMacExports = [FREDDIEMAC_BACE_EXPORT, FREDDIE_MAC_UPD_FEATURE_DRIVEN_EXPORT];
  const fannieAndFreddieExports = [...fannieMaeExports, ...freddieMacExports];

  const signalRStateRef = useRef({
    oldState: SignalRConnectionStatus_Disconnected,
    newState: connectionStatus_NotConnected
  });

  let mounted = useRef(true);
  const signalrConnectionHub = useRef(null);
  const signalR_ReconnectDelay = useRef(1000);
  const connectionStatus_NotConnected = 999;
  const runningExports_Ref = useRef({});

  useEffect(() => {
    refresh();
    startSignalRConnection();

    return () => {
      mounted.current = false;
      runningExports_Ref.current = {};
      // Disconnect SignalR on component unmount
      const localSignalRHub = signalrConnectionHub.current;
      signalrConnectionHub.current = null;
      localSignalRHub && localSignalRHub.stop(true, true);
    };
  }, []);

  useEffect(() => {
    setExportInfo({
      loanFreddieMacCaseFileId: caseInfo.loanFreddieMacCaseFileId,
      loanDesktopUnderwriterCaseFileId: caseInfo.loanDesktopUnderwriterCaseFileId,
      integrationFannieMaeWaiver: caseInfo.integrationFannieMaeWaiver,
      integrationFreddieMacBace: caseInfo.integrationFreddieMacBace,
    });
    refresh();
  }, [caseInfo]);

  useEffect(() => {
    let running = {...runningExports[controlNumber]};
    if(running && Object.keys(running).length){
      setRunningExportsCurrent(runningExports[controlNumber]);
    } else {
      setRunningExportsCurrent({});
    }
    runningExports_Ref.current = runningExports;
  }, [runningExports]);

  const retrieveExportDefs = () => {
    setFetchingExportDefs(true);
    getAvailableExports(controlNumber, (availResp) => {
      setExportDefs(availResp.availableExportCodes);
      setFetchingExportDefs(false);
    }, () => {setFetchingExportDefs(false);}, mounted);
  };

  const retrieveExportResults = () => {
    setFetchingExportResults(true);
    getExportResults(controlNumber, (resultResp) => {
      setExportResults(resultResp.caseExportResults);
      setFetchingExportResults(false);
      removeLingeringExports(resultResp.caseExportResults);
    }, () => {
      setFetchingExportResults(false);
    }, mounted);
  };

  // In case an export finished while away from the tab, remove
  const removeLingeringExports = (latestExportResults) => {
    let running = {...runningExports};
    if(running && running[controlNumber]){
      Object.keys(running[controlNumber]).forEach(code => {
        let lastExportResult = latestExportResults.filter(p => p.exportCode === code)
          .sort((a,b) => a.exportStartedAtUtc > b.exportStartedAtUtc ? 1 : -1)[0];
        if(lastExportResult){
          // Some export rows say they were finished before the last message was actually sent by the export.
          // Add 5 seconds on to allow wiggle room between the last message TimeStamp date and the export finish date
          const exportFinishedAt = moment(new Date(lastExportResult.exportFinishedAtUtc + "Z"));
          exportFinishedAt.add(5, "seconds");
          if(running[controlNumber][`${code}_TimeStamp`] <= exportFinishedAt.toDate()){
            removeRunningExports({ControlNumber: controlNumber, ExportCode: code});
          }
        }
      });
    }
  };

  const retrieveWaiverResults = () => {
    setFetchingWaiverResults(true);
    getWaiverResults(controlNumber, (resultResp) => {
      setWaiverResults(resultResp);
      setFetchingWaiverResults(false);
    }, () => {setFetchingWaiverResults(false);}, mounted);
  };

  const refresh = () => {
    retrieveExportDefs();
    retrieveExportResults();
    retrieveWaiverResults();
  };

  function startSignalRConnection() {
    if (signalRDisconnected(signalRStateRef.current.newState)) {
      // Reconnect SignalR if the connection is disconnected
      signalrConnectionHub.current = null;
    }
    if (!signalrConnectionHub.current) {
      // Create a new SignalR connection
      let connectionFunc = setupSignalRConnection(
          WebConfig.signalrConnectionRoot, "CaseExportHub",
          {
            SSO: signalrToken
          },
          {
            ReceiveCaseExportProgressUpdate: onReceiveCaseExportProgressUpdate,
          },
          () => {
            return signalrToken;
          });
      signalrConnectionHub.current = initSignalR(connectionFunc)
          .then((v) => {
            if (!v.events.onStateChanged) {
              v.events.onStateChanged = [];
            }
            v.events.onStateChanged.push(signalRStateChanged);
            v.reconnectWindow = Math.max(v.reconnectWindow, 120000); // Try reconnecting for at least 120 seconds
            v.serverTimeoutInMilliseconds = Math.max(v.serverTimeoutInMilliseconds, v.reconnectWindow * 2, 240000); // Try reconnecting for at least 120 seconds
            signalrConnectionHub.current = v;
          });
    }
  }

  const resubscribeRunning = () => {
    if(signalrConnectionHub.current){
      let running = {...runningExports};
      if(running[controlNumber]){
        for (const [key, value] of Object.entries(running[controlNumber])) {
          if(!key.includes("_TimeStamp")) subscribeExport(value);
        }
      }
    }
  };

  const signalRStateChanged = (e, data) => {
    signalRStateRef.current = data;
    if (data.newState === SignalRConnectionStatus_Connected){
      setSignalRConnected(true);
      signalR_ReconnectDelay.current = 1000;
      resubscribeRunning();
    }
    if (signalRDisconnected(data.newState)) {
      setSignalRConnected(false);
      window.setTimeout(() => {
        if(signalrConnectionHub.current){
          signalrConnectionHub.current.start();
        }
      }, signalR_ReconnectDelay.current);
      signalR_ReconnectDelay.current += 1000;
    }
    if(signalRReconnecting(data.newState)) {
      setSignalRConnected(false);
    }
  };

  const signalRDisconnected = (state) => {
    return signalrConnectionHub.current && state === SignalRConnectionStatus_Disconnected;
  };

  const signalRReconnecting = (state) => {
    return signalrConnectionHub.current && state === SignalRConnectionStatus_Reconnecting;
  };

  const onReceiveCaseExportProgressUpdate = (message) => {
    if (message.ControlNumber == controlNumber && mounted.current) {
      if(message.ExportFinished){
        removeRunningExports(message);
        makeToast(`${message.ExportCode} Finished`, "success");
        let messages = {...exportMessages};
        delete messages[message.ExportCode];
        setExportMessages(messages);
        retrieveExportResults();
        retrieveWaiverResults();
      } else if (!message.ExportSequenceFinished) {
          if(message.UpdateMessage === "Loading Data") {
            message.UpdateMessage = "Loading Export Data";
          }
          message.UpdateMessage = `${message.UpdateMessage}...`;
          addRunningExports(message);
          let messages = {...exportMessages};
          messages[message.ExportCode] = message;
          setExportMessages(messages);
      }
    }
  };

  const addRunningExports = (message) => {
    // global.structuredClone is here to avoid mutating objects from state
    let running = global.structuredClone(runningExports_Ref.current);
    if(running[message.ControlNumber]){
      running[message.ControlNumber][message.ExportCode] = message.ExportJobUuid;
    } else {
      running[message.ControlNumber] = {};
      running[message.ControlNumber][message.ExportCode] = message.ExportJobUuid;
    }
    if (message.CreateDateUtc){
      // Prefer the date from the message object when available
      running[message.ControlNumber][`${message.ExportCode}_TimeStamp`] = new Date(message.CreateDateUtc);
    } else{
      running[message.ControlNumber][`${message.ExportCode}_TimeStamp`] = new Date();
    }
    setRunningExports(running);
  };

  const removeRunningExports = (message) => {
    // global.structuredClone is here to avoid mutating objects from state
    let running = global.structuredClone(runningExports_Ref.current);
    if(running[message.ControlNumber]){
      delete running[message.ControlNumber][message.ExportCode];
      delete running[message.ControlNumber][`${message.ExportCode}_TimeStamp`];
    }
    setRunningExports(running);
  };

  const runAllExports = () => {
    let toRun = [];
    let running = {...runningExportsCurrent};
    exportDefs.forEach(e => {
      if(running[e]) {
        // do nothing
      } else {
        forceExportToRun(e, null);
        toRun.push(e);
      }
    });
    if(!toRun.length) {
      makeToast("All Exports already running", "error");
    } else {
      sendMultipleExports(running, toRun);
      makeToast("All Exports Started", "success");
    }
  };

  const sendMultipleExports = (running, toRun) => {
    startExport(controlNumber, toRun, (resp) => {
      resp.initiateRealtimeExportResults.forEach(e => {
        e.UpdateMessage = "Pending Export...";
        e.ExportCode = e.exportCode;
        e.ExportJobUuid = e.exportJobUuid;
        e.ControlNumber = e.controlNumber;
        running[e.exportCode] = e.exportJobUuid;
        subscribeExport(running[e.exportCode]);
        let messages = {...exportMessages};
        messages[e.exportCode] = e;
        setExportMessages(messages);
        addRunningExports(e);
      });
      setRunningExportsCurrent(running);
    }, () => {}, mounted);
  };

  const runExport = (e) => {
    let running = {...runningExportsCurrent};
    if(running[e]) {
      makeToast("Export Already Running", "error");
    } else {
      forceExportToRun(e, () => sendSingleExport(e));
    }
  };

  const forceExportToRun = (e, callback) => {
    markExportForRetry(controlNumber, {
      ControlNumber: controlNumber,
      ExportCode: e
    }, () => {
      if(callback) callback();
    }, () => {}, mounted);
  };

  const sendSingleExport = (e) => {
    startExport(controlNumber, [e], (resp) => {
      let message = resp.initiateRealtimeExportResults.filter(s => s.exportCode === e)[0];
      message.UpdateMessage = "Pending Export...";
      message.ExportCode = message.exportCode;
      message.ExportJobUuid = message.exportJobUuid;
      message.ControlNumber = message.controlNumber;
      subscribeExport(message.ExportJobUuid);
      addRunningExports(message);
      let messages = {...exportMessages};
      messages[e] = message;
      setExportMessages(messages);
      makeToast("Export Started", "success");
    }, () => {}, mounted);
  };

  const getExistingExport = (e) => {
    return exportResults.filter(p => p.exportCode === e).sort((a,b) => a.exportStartedAtUtc > b.exportStartedAtUtc ? 1 : -1)[0];
  };

  const subscribeExport = (uuid) => {
    const exportUuid = [uuid];
    signalrConnectionHub.current.createHubProxy("CaseExportHub")
        .invoke("subscribeExports", exportUuid);
  };

  const Loading = () => {
    return (
      <div style={{margin: 'auto'}}>
        <MSpinner />
        {!signalRConnected && <div><i>Connecting to Server...</i></div>}
      </div>
    );
  };

  const ExportDefButtons = () => {
    if(fetchingExportDefs || !signalRConnected) return <Loading />;
    if(exportDefs.length){
      let exportBtns = [
        <div key="run-all" className="export-button-group">
          <Button
            id="run_all_exports_button"
            disabled={Object.keys(runningExportsCurrent).length > 0}
            onClick={() => runAllExports()}
            className="m-t-10 case-export-button"
          >
            Run All Exports
          </Button>
       </div>
      ];
      exportDefs.forEach(s =>
        exportBtns.push(
        <div key={s} className="export-button-group">
          <Button
            id={`${s}_button`}
            onClick={() => runExport(s)}
            disabled={!!runningExportsCurrent[s]}
            className="m-t-10 case-export-button"
          >
            {s.replaceAll("_", " ")}
          </Button>
        </div>)
      );
      return (exportBtns);
    } else {
      return (
        <>
          <div style={{margin: '10px'}}><b>No Exports available for this case</b></div>
          <div />
          <Button
            id="refresh"
            onClick={() => refresh()}
            className="m-t-10 refresh-button"
          >
            refresh
          </Button>
        </>
      );
    }
  };

  const getExportResultFields = (result) => {
    let results = [];
    if(!runningExportsCurrent[result.exportCode]){
      results.push({
        key: `${result.caseExportResultId}_status`,
        value: result.exportSuccessful ? "Successful" : "Resubmission Required",
        label: "Status"
      },{
        key: `${result.caseExportResultId}_date`,
        value: new Date(result.exportFinishedAtUtc + "Z"),
        label: "Date",
        dataType: DATA_TYPE_DATE_TIME
      },{
        key: `${result.caseExportResultId}_result`,
        value: result.exportProcessingSummary,
        label: "Result"
      });
      if(!result.exportSuccessful){
        results.push({
          key: `${result.caseExportResultId}_details`,
          value: result.exportResultsText,
          label: "Details",
          dataType: DATA_TYPE_MULTI_LINE_STRING
        });
      }
    }
    if(exportMessages[result.exportCode]){
      results.push({
        key: `${result.caseExportResultId}_progress`,
        value: exportMessages[result.exportCode].UpdateMessage,
        label: "Export Progress"
      });
    } else if (runningExportsCurrent[result.exportCode]){
      results.push({
        key: `${result.caseExportResultId}_progress`,
        value: "Export in Progress...",
        label: "Export Progress"
      });
    }
    return results;
  };

  const getSubmissionRequiredFields = (code) => {
      let results = [{
        key: `${code}_status`,
        value: "Submission Required",
        label: "Status"
      }];
      if(exportMessages[code]){
        results.push({
          key: `${code}_progress`,
          value: exportMessages[code].UpdateMessage,
          label: "Export Progress"
        });
      }
    return results;
  };

  const formatFreddieMacMessages = (messages) => {
    if (!messages?.length) return "";
    let retVal = "";
    for (let i = 0; i < messages.length; i++) {
      const message = messages[i];
      if (message.isMainMessage) continue;
      if (!message.message) continue;
      retVal = `${retVal}\n${message.message}`;
      if (message?.context?.label) {
        retVal = `${retVal} in ${message.context.label}`;
      }
    }
    return retVal.trim();
  };

  const onExportInfoSaveCallback = () => {
    makeToast("Save Success", "success");
    getCaseHeader(controlNumber, () => setIsSavingCaseHeaders(false));
  };

  const onExportInfoSave = () => {
    setIsSavingCaseHeaders(true);
    let editedObj = cardControlMethods.current.getEditedDataObject();
    if (editedObj)
      updateCaseHeader(controlNumber, editedObj, onExportInfoSaveCallback, mounted);
  };

  const NonWaiverExportResults = () => {
    if(fetchingExportResults) return <MSpinner />;
    let exportsToUse = exportDefs.filter(s =>
        !fannieAndFreddieExports.includes(s)
    );

    return (
      <>
        {exportsToUse.map(s => {
          let existingExport = getExistingExport(s);
          if(existingExport && existingExport.exportCode) {
            return (
              <MCard
                key={`${s}_card`}
                title={existingExport.exportName}
                className={"mcard case-export-result"}
                fields={getExportResultFields(existingExport)}
                actions={exportMessages[existingExport.exportCode]
                  ? [] :[{
                  icon: "refresh",
                  helpText: "Refresh",
                  order: 9002,
                  onClick: () => refresh()
                }]}
              />);
          } else {
            return (
            <MCard
              key={`${s}_card`}
              title={s.replaceAll("_", " ")}
              className={"mcard case-export-result"}
              fields={getSubmissionRequiredFields(s)}
              actions={[{
                icon: "refresh",
                helpText: "Refresh",
                order: 9002,
                onClick: () => refresh()
              }]}
            />);
          }})}
      </>
    );
  };

  const WaiverExportResults = ({code}) => {
    const codes = [...code];
    if(fetchingExportResults) return <MSpinner />;
    let exportsToUse = exportDefs.filter(s => codes.includes(s));
    return (
      <>
        {exportsToUse.map(s => {
          let existingExport = getExistingExport(s);
          if(existingExport && existingExport.exportCode) {
            return (
              <MCard
                key={`${s}_card`}
                title={existingExport.exportName}
                className={"mcard case-export-result"}
                fields={getExportResultFields(existingExport)}
                actions={exportMessages[existingExport.exportCode]
                  ? [] :[{
                  icon: "refresh",
                  helpText: "Refresh",
                  order: 9002,
                  onClick: () => refresh()
                }]}
              />);
          } else {
            return (
            <MCard
              key={`${s}_card`}
              title={s.replaceAll("_", " ")}
              className={"mcard case-export-result"}
              fields={getSubmissionRequiredFields(s)}
              actions={[{
                icon: "refresh",
                helpText: "Refresh",
                order: 9002,
                onClick: () => refresh()
              }]}
            />);
          }})}
      </>
    );
  };

  WaiverExportResults.propTypes = {
    code: PropTypes.string | PropTypes.array,
  };

  // Feels like this should have been defined some way in the back-end like the other
  // export data, would be easier to group like results and shape the data
  // as needed..
  const WaiverResults = () => {
    if (fetchingWaiverResults || fetchingExportResults) return <MSpinner/>;
    const showFannieMaeWaiverResults = waiverResults?.fannieMaeResults &&
        exportResults?.find(r => r.exportCode === FANNIE_MAE_FIELD_SURVEY_FEATURE_DRIVEN_EXPORT || r.exportCode === FNMA_UPD_FEATURE_DRIVEN_EXPORT) &&
        waiverResults?.fannieMaeResults?.responseDate &&
        moment(waiverResults?.fannieMaeResults?.responseDate)?.year() > 1;
    let fannieMaeFields = [];
    if (showFannieMaeWaiverResults) {
      const code = "fannieMae";
      fannieMaeFields = [
        {
          key: `${code}_date`,
          label: "Date",
          value: moment(waiverResults.fannieMaeResults.responseDate).toDate(),
          dataType: DATA_TYPE_DATE_TIME
        },
        {
          key: `${code}_message`,
          label: "Message",
          value: waiverResults.fannieMaeResults.appraisalDecision.description
        },
      ];
      const fannieMaeMessages = waiverResults.fannieMaeResults.propertyDataMessages.map((m) => {
        return {
          key: `${code}_propertyDataMessages_${m.id}`,
          label: `Message Code: ${m.fnmaMessageCode}`,
          value: m.fnmaMessageDescription,
          dataType: DATA_TYPE_MULTI_LINE_STRING
        };
      });
      const fannieMaePhotoMessages = waiverResults.fannieMaeResults.propertyDataPhotoMessages.map((m) => {
        return {
          key: `${code}_propertyDataPhotoMessages_${m.id}`,
          label: `Photo Message Code: ${m.fnmaMessageCode}`,
          value: `${m.fnmaPropertyDataPhotoPath} - ${m.fnmaMessageDescription} (Photo Id ${m.photoId})`,
          dataType: DATA_TYPE_MULTI_LINE_STRING
        };
      });
      fannieMaeFields = fannieMaeFields.concat(fannieMaeMessages.concat(fannieMaePhotoMessages));
    }
    let showFreddieMacWaiverResults = waiverResults?.freddieMacResults &&
        exportResults?.find(r => r.exportCode === FREDDIEMAC_BACE_EXPORT || r.exportCode === FREDDIE_MAC_UPD_FEATURE_DRIVEN_EXPORT)?.exportSuccessful;
    let freddieMacFields = [];
    if (showFreddieMacWaiverResults) {
      const mainMessageCategory = waiverResults.freddieMacResults.messageCategories
          ?.filter(v => v.messages.filter(m => m.isMainMessage).length);
      const code = "freddieMac";
      freddieMacFields = [
        {
          key: `${code}_date`,
          label: "Date",
          value: moment(waiverResults.freddieMacResults.freddieMacBaceResponse.resultsDateUtc).toDate(),
          dataType: DATA_TYPE_DATE_TIME
        },
        {
          key: `${code}_message`,
          label: (mainMessageCategory?.length ? mainMessageCategory[0]?.category : null) ?? "Message",
          value: waiverResults.freddieMacResults.message,
          dataType: DATA_TYPE_MULTI_LINE_STRING
        }
      ];
      const messageFields = waiverResults.freddieMacResults.messageCategories
          .map((c) => {
            const formattedMessages = formatFreddieMacMessages(c.messages);
            if (!formattedMessages) return null;
            return {
              key: `${code}_details_{c.category}`,
              label: c.category,
              dataType: DATA_TYPE_MULTI_LINE_STRING,
              value: formattedMessages,
            };
          })
          .filter(v => v != null);
      freddieMacFields = freddieMacFields.concat(messageFields);
    }
    return (
      <>
        <MCard
          key="export_info_card"
          className="md-cell md-cell--12"
          title="Export IDs"
          isEditable={true}
          onEditFinished={onExportInfoSave}
          getControlMethods={cm => cardControlMethods.current = cm}
          isFetching={isSavingCaseHeaders}
          fields={[
            {
              key: "CaseInfo.LoanDesktopUnderwriterCaseFileId", label: "Fannie Mae - DU ID", value: exportInfo.loanDesktopUnderwriterCaseFileId, dataType: DATA_TYPE_STRING
            },
            {
              key: "CaseInfo.IntegrationFannieMaeWaiver", label: "Integration - Fannie Mae PDA", value: exportInfo.integrationFannieMaeWaiver === "True" ?? false, dataType: DATA_TYPE_BOOLEAN,
            },
            {
              key: "CaseInfo.LoanFreddieMacCaseFileId", label: "Freddie Mac - LP ID", value: exportInfo.loanFreddieMacCaseFileId, dataType: DATA_TYPE_STRING,
            },
            {
              key: "CaseInfo.IntegrationFreddieMacBace", label: "Integration - Freddie Mac BACE", value: exportInfo.integrationFreddieMacBace === "True" ?? false, dataType: DATA_TYPE_BOOLEAN,
            },
          ]}
        />
        {exportDefs.filter(s => fannieMaeExports.includes(s))[0] &&
          <Card className="md-cell md-cell--12">
            <CardContent
              onClick={() => setToggleFannieMaeCard(!toggleFannieMaeCard)}
              style={{ cursor: "pointer", padding: "5px", display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <Typography variant="h6">Fannie Mae Exports</Typography>
              <IconButton size="small">
                <ExpandMoreIcon className={`expand-icon ${toggleFannieMaeCard ? 'expanded' : ''}`} />
              </IconButton>
            </CardContent>
            <Collapse collapsed={!toggleFannieMaeCard}>
              <>
                <WaiverExportResults code={fannieMaeExports} />
                {showFannieMaeWaiverResults &&
                  <MCard
                    key={"fannieMaeCard"}
                    title={"Fannie Mae Results"}
                    className={"mcard case-waiver-result fannieMae"}
                    fields={fannieMaeFields}
                  />}
              </>
            </Collapse>
          </Card>}
        {exportDefs.filter(s => freddieMacExports.includes(s))[0] &&
          <Card className="md-cell md-cell--12">
            <CardContent
              onClick={() => setToggleFreddieMacCard(!toggleFreddieMacCard)}
              style={{ cursor: "pointer", padding: "5px", display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <Typography variant="h6">Freddie Mac Exports</Typography>
              <IconButton size="small">
                <ExpandMoreIcon className={`expand-icon ${toggleFreddieMacCard ? 'expanded' : ''}`} />
              </IconButton>
            </CardContent>
            <Collapse collapsed={!toggleFreddieMacCard}>
              <CardContent>
                <WaiverExportResults code={freddieMacExports} />
                {showFreddieMacWaiverResults &&
                  <MCard
                    key={"freddieMacCard"}
                    title={"Freddie Mac Results"}
                    className={"mcard case-waiver-result freddieMac"}
                    fields={freddieMacFields}
                  />}
              </CardContent>
            </Collapse>
          </Card>}
      </>
    );
  };

  return (
    <div className="case-exports">
      <div className="export-buttons">
        <ExportDefButtons />
      </div>
      <div style={{margin: '10px'}}/>
      <div className="waiver-results">
        <WaiverResults />
      </div>
      <div className="export-results">
        <NonWaiverExportResults />
      </div>
      <div style={{margin: '10px'}}/>
      {showNotifications && <Notifications controlNumber={controlNumber} />}
    </div>
  );

};

Exports.propTypes = {
  controlNumber: PropTypes.number,
  signalrToken: PropTypes.string,
  initSignalR: PropTypes.func,
  makeToast: PropTypes.func,
  showNotifications: PropTypes.bool,
  runningExports: PropTypes.object,
  setRunningExports: PropTypes.func,
  caseInfo: PropTypes.object,
  getCaseHeader: PropTypes.func,
};

const mapStateToProps = (state) => ({
  signalrToken: state.user.profile.token,
  showNotifications: state.user.profile.rights.includes(CASE_NOTIFICATIONS),
  runningExports: state.exports.runningExports,
  caseInfo: state.caseForms.caseHeader.caseInfo,
});

const mapDispatchToProps = (dispatch) => ({
  initSignalR(signalrConnectionHub) {
    return dispatch(signalrConnectionHub);
  },
  makeToast(text, type) {
    responseHandler.customToast(text, type, dispatch);
  },
  setRunningExports(message){
    return dispatch(setRunningExports(message));
  },
  getCaseHeader(controlIdNumber, callback) {
    dispatch(getCaseHeader(controlIdNumber, callback));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Exports);
